/**
 * Color utility functions for manipulating hex colors and creating RGBA values
 */

/**
 * Convert hex color to rgba with specified alpha
 * @param hex - Hex color string (with or without #)
 * @param alpha - Alpha value between 0 and 1
 * @returns RGBA color string or original hex if invalid
 */
export function softenHex(hex: string, alpha: number = 0.2): string {
  if (!hex || typeof hex !== "string") {
    return hex;
  }

  const h = hex.replace(/^#/, "");

  // Handle 3-character hex (e.g., #RGB)
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return hex; // fallback for invalid hex
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Handle 6-character hex (e.g., #RRGGBB)
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return hex; // fallback for invalid hex
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Return original for invalid format
  return hex;
}

/**
 * Validate if a string is a valid hex color
 */
export function isValidHex(hex: string): boolean {
  if (!hex || typeof hex !== "string") {
    return false;
  }

  const h = hex.replace(/^#/, "");
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(h);
}

/**
 * Convert RGB values to hex string
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Parse hex color to RGB values
 */
export function hexToRgb(
  hex: string,
): { r: number; g: number; b: number } | null {
  if (!isValidHex(hex)) {
    return null;
  }

  const h = hex.replace(/^#/, "");

  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }

  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  return null;
}

/**
 * Alias for isValidHex with full hex color validation
 */
export function isValidHexColor(color: string | null | undefined): boolean {
  if (!color) return false;
  return isValidHex(color);
}

export function isLightColor(hexColor: string): boolean {
  // Remove # if present
  const color = hexColor.replace("#", "");

  // Convert to RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);

  // Calculate brightness using relative luminance formula
  const brightness = r * 0.299 + g * 0.587 + b * 0.114;

  return brightness > 128;
}
